import { uploadQueueDB, QueuedUpload, QueuedFile } from '../db/upload-queue.db';
import api from '../api/auth.api';
import { API_CONFIG } from '../config/api.config';
import { App } from '@capacitor/app';
import { isNativeApp } from '../utils/platform.utils';
import { backgroundUploadService, BackgroundUploadOptions } from './background-upload.service';
import { computeFileHash, isDuplicateHash, storeUploadHash } from '../utils/hash.utils';

// Generate unique tab ID for this browser tab
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const LOCK_ID = 'upload-queue-lock';
const LOCK_TIMEOUT_MS = 60000; // 60 seconds - lock expires if tab crashes

class UploadQueueService {
  private isProcessing = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private appListenersInitialized = false;

  /**
   * Try to acquire processing lock (prevents multiple tabs from processing)
   */
  private async acquireLock(): Promise<boolean> {
    try {
      const now = Date.now();
      const existingLock = await uploadQueueDB.locks.get(LOCK_ID);

      // Check if lock exists and is still valid
      if (existingLock) {
        if (existingLock.tabId === TAB_ID) {
          // We already have the lock, extend it
          await uploadQueueDB.locks.put({
            id: LOCK_ID,
            tabId: TAB_ID,
            acquiredAt: existingLock.acquiredAt,
            expiresAt: now + LOCK_TIMEOUT_MS
          });
          return true;
        }

        if (existingLock.expiresAt > now) {
          // Another tab has a valid lock
          console.log(`[UploadQueue] Lock held by another tab, skipping`);
          return false;
        }

        // Lock expired, we can take it
        console.log(`[UploadQueue] Taking over expired lock`);
      }

      // Acquire lock
      await uploadQueueDB.locks.put({
        id: LOCK_ID,
        tabId: TAB_ID,
        acquiredAt: now,
        expiresAt: now + LOCK_TIMEOUT_MS
      });

      return true;
    } catch (error) {
      console.error('[UploadQueue] Lock acquisition error:', error);
      return false;
    }
  }

  /**
   * Release processing lock
   */
  private async releaseLock(): Promise<void> {
    try {
      const existingLock = await uploadQueueDB.locks.get(LOCK_ID);
      if (existingLock?.tabId === TAB_ID) {
        await uploadQueueDB.locks.delete(LOCK_ID);
      }
    } catch (error) {
      console.error('[UploadQueue] Lock release error:', error);
    }
  }

  /**
   * Add an upload to the queue
   * For native iOS/Android: Uses background upload service (continues even when app closed)
   * For PWA: Uses IndexedDB queue with foreground processing
   */
  async addToQueue(
    type: 'photo' | 'asphalt_photo' | 'truck_ticket' | 'daily_report' | 'tm_ticket' | 'equipment_issue' | 'asphalt_report' | 'equipment_repair' | 'equipment_repair_update' | 'receipt',
    data: any,
    files: { file: Blob; filename: string; fieldName: string; base64?: string }[],
    employeeId: number,
    jobId?: number
  ): Promise<number> {
    // Validate files before queueing - prevents corrupt/empty uploads
    if (files && files.length > 0) {
      for (const fileData of files) {
        const fileSize = fileData.file?.size || 0;
        if (!fileData.file || fileSize === 0) {
          console.error(`[UploadQueue] Rejected empty file: ${fileData.filename}`);
          throw new Error(`File ${fileData.filename} is empty or invalid`);
        }
        // Images should be at least 1KB to be valid
        if (fileSize < 1000 && fileData.file.type?.startsWith('image/')) {
          console.error(`[UploadQueue] Rejected suspiciously small image: ${fileData.filename} (${fileSize} bytes)`);
          throw new Error(`Image ${fileData.filename} appears to be corrupted (only ${fileSize} bytes)`);
        }
        console.log(`[UploadQueue] File validated: ${fileData.filename}, size: ${fileSize} bytes`);
      }
    }

    // NATIVE APP: Use background upload service (iOS URLSession / Android WorkManager)
    // Uploads continue even when app is closed
    if (isNativeApp() && files && files.length > 0) {
      console.log(`[UploadQueue] Using native background upload for ${type}`);
      // Add to global debug log for UI display
      (window as any).__uploadDebug = (window as any).__uploadDebug || [];
      (window as any).__uploadDebug.push(`[Queue] Native upload for ${type}, ${files.length} files`);
      if ((window as any).__uploadDebug.length > 50) (window as any).__uploadDebug.shift();

      // Content-hash based duplicate detection for native uploads
      // Computes hash before upload to prevent duplicate photos
      for (const fileData of files) {
        try {
          const fileHash = await computeFileHash(fileData.file);
          if (isDuplicateHash(fileHash, jobId || 0, type)) {
            console.log(`[UploadQueue] Duplicate detected by content hash (${fileHash}), skipping native upload for ${fileData.filename}`);
            return -3; // -3 indicates duplicate detected
          }
          // Store hash immediately to prevent race conditions
          storeUploadHash(fileHash, jobId || 0, type);
          console.log(`[UploadQueue] Content hash computed for native upload: ${fileHash}`);
        } catch (hashError) {
          console.warn(`[UploadQueue] Could not compute hash, proceeding without dedup:`, hashError);
        }
      }

      // Parse capture time from data
      const captureTime = data.time_received ? new Date(data.time_received) : new Date();

      // Upload each file using background service
      for (const fileData of files) {
        const options: BackgroundUploadOptions = {
          type,
          jobId,
          captureTime,
          metadata: {
            ...data,
            employee_id: employeeId
          }
        };

        (window as any).__uploadDebug?.push(`[Queue] Calling backgroundUploadService.uploadFile...`);
        (window as any).__uploadDebug?.push(`[Queue] base64 provided: ${fileData.base64 ? 'YES ' + fileData.base64.length + ' chars' : 'NO'}`);

        const result = await backgroundUploadService.uploadFile(
          fileData.file,
          fileData.filename,
          options,
          undefined, // onProgress
          fileData.base64 // Pass base64 directly to avoid double conversion on iPad
        );

        (window as any).__uploadDebug?.push(`[Queue] Upload result: success=${result.success}, id=${result.id}, error=${result.error || 'none'}`);

        if (!result.success && result.id !== -1 && result.id !== -4) {
          // -1 means upload is continuing in background, which is OK
          // -4 means saved to native offline queue, which is also OK
          console.error(`[UploadQueue] Background upload failed:`, result.error);
          (window as any).__uploadDebug?.push(`[Queue] FAILED - falling through to IndexedDB`);
          // Fall through to IndexedDB queue as backup
          break;
        }

        if (result.id && result.id > 0) {
          console.log(`[UploadQueue] Background upload completed immediately: ${result.id}`);
          (window as any).__uploadDebug?.push(`[Queue] SUCCESS immediate: ${result.id}`);
          return result.id;
        }

        if (result.id === -1) {
          console.log(`[UploadQueue] Background upload started, continuing in background`);
          (window as any).__uploadDebug?.push(`[Queue] Background started (-1)`);
          return -1; // Indicate background upload in progress
        }

        if (result.id === -4) {
          console.log(`[UploadQueue] Saved to native offline queue, will retry when network available`);
          (window as any).__uploadDebug?.push(`[Queue] Native offline queue (-4)`);
          return -4; // Indicate native offline queue
        }
      }
    }

    // PWA FALLBACK: Use IndexedDB queue with foreground processing
    // Content-hash based duplicate detection (checks localStorage hash cache)
    if (files && files.length > 0) {
      for (const fileData of files) {
        try {
          const fileHash = await computeFileHash(fileData.file);
          if (isDuplicateHash(fileHash, jobId || 0, type)) {
            console.log(`[UploadQueue] Duplicate detected by content hash (${fileHash}), skipping PWA upload for ${fileData.filename}`);
            return -3; // -3 indicates duplicate detected
          }
          // Store hash immediately to prevent race conditions
          storeUploadHash(fileHash, jobId || 0, type);
          console.log(`[UploadQueue] Content hash computed for PWA upload: ${fileHash}`);
        } catch (hashError) {
          console.warn(`[UploadQueue] Could not compute hash, proceeding without dedup:`, hashError);
        }
      }
    }

    // Legacy filename-based check as fallback (10 second window)
    const recentCutoff = new Date(Date.now() - 10000);
    const firstFilename = files && files.length > 0 ? files[0].filename : null;

    if (firstFilename) {
      // Get existing uploads for this type/job/employee
      const existingUploads = await uploadQueueDB.uploads
        .where('type')
        .equals(type)
        .and(upload =>
          upload.jobId === jobId &&
          upload.employeeId === employeeId &&
          upload.createdAt && upload.createdAt > recentCutoff &&
          (upload.status === 'pending' || upload.status === 'uploading')
        )
        .toArray();

      // Check if any existing upload has the same filename (true duplicate)
      for (const upload of existingUploads) {
        if (upload.id) {
          const existingFiles = await uploadQueueDB.files
            .where('uploadId')
            .equals(upload.id)
            .toArray();

          if (existingFiles.some(f => f.filename === firstFilename)) {
            console.log(`[UploadQueue] True duplicate detected (same filename: ${firstFilename}), returning existing ID: ${upload.id}`);
            return upload.id;
          }
        }
      }
      console.log(`[UploadQueue] No duplicate found - different filename: ${firstFilename}`);
    }

    // Add upload record to IndexedDB
    const uploadId = await uploadQueueDB.uploads.add({
      type,
      status: 'pending',
      data,
      jobId,
      employeeId,
      createdAt: new Date(),
      retryCount: 0
    });

    // Add associated files - store as base64 to avoid iOS IndexedDB Blob corruption
    if (files && files.length > 0) {
      for (const fileData of files) {
        // Use provided base64 directly if available (avoids double-conversion on iOS native)
        // Otherwise convert Blob to base64 for reliable storage
        let base64: string;
        if (fileData.base64) {
          base64 = fileData.base64;
          console.log(`[UploadQueue] Using provided base64 directly: ${fileData.filename}, ${base64.length} chars`);
        } else {
          base64 = await this.blobToBase64(fileData.file);
          console.log(`[UploadQueue] Converted blob to base64: ${fileData.filename}, ${base64.length} chars`);
        }

        await uploadQueueDB.files.add({
          uploadId,
          file: new Blob(), // Empty blob placeholder (we use fileBase64)
          fileBase64: base64,
          filename: fileData.filename,
          fieldName: fileData.fieldName
        });
        console.log(`[UploadQueue] File stored: ${fileData.filename}`);
      }
    }

    // Trigger immediate sync attempt
    this.processQueue();

    return uploadId;
  }

  /**
   * Process the upload queue
   */
  async processQueue() {
    if (this.isProcessing) return;

    // Try to acquire lock (prevents multiple tabs from processing)
    const hasLock = await this.acquireLock();
    if (!hasLock) {
      console.log('[UploadQueue] Could not acquire lock, another tab is processing');
      return;
    }

    this.isProcessing = true;

    try {
      // Get all pending uploads
      const pendingUploads = await uploadQueueDB.uploads
        .where('status')
        .equals('pending')
        .or('status')
        .equals('failed')
        .and(upload => upload.retryCount < 3) // Max 3 retries
        .toArray();

      for (const upload of pendingUploads) {
        // Extend lock while processing
        await this.acquireLock();
        await this.processUpload(upload);
      }

      // Trigger background sync if service worker is available
      this.triggerBackgroundSync();

    } catch (error) {
      console.error('Queue processing error:', error);
    } finally {
      this.isProcessing = false;
      await this.releaseLock();
    }
  }

  /**
   * Trigger background sync via Service Worker
   */
  private async triggerBackgroundSync() {
    try {
      if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('upload-queue-sync');
        console.log('Background sync registered');
      }
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }

  /**
   * Process a single upload
   */
  private async processUpload(upload: QueuedUpload) {
    if (!upload.id) return;

    // Check if the upload still exists and is still pending/failed (not already uploading or completed)
    // This prevents race conditions where multiple processes pick up the same item
    const currentUpload = await uploadQueueDB.uploads.get(upload.id);
    if (!currentUpload) {
      console.log(`[UploadQueue] Upload ${upload.id} no longer exists, skipping`);
      return;
    }
    if (currentUpload.status === 'uploading') {
      console.log(`[UploadQueue] Upload ${upload.id} is already being processed, skipping`);
      return;
    }

    try {
      // Update status to uploading
      await uploadQueueDB.uploads.update(upload.id, { status: 'uploading' });

      // Get associated files
      const files = await uploadQueueDB.files
        .where('uploadId')
        .equals(upload.id)
        .toArray();

      // Validate files before upload - detect corruption in IndexedDB storage
      for (const fileData of files) {
        // Check base64 if available (new format), otherwise check blob
        const hasValidData = fileData.fileBase64 ? fileData.fileBase64.length > 100 : (fileData.file && fileData.file.size > 0);

        if (!hasValidData) {
          console.error(`[UploadQueue] Corrupt file detected in queue: ${fileData.filename}, removing upload ${upload.id}`);
          // Remove corrupt upload from queue
          await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
          await uploadQueueDB.uploads.delete(upload.id);
          return; // Don't retry corrupt uploads
        }

        // Check for suspiciously small images
        const dataSize = fileData.fileBase64 ? fileData.fileBase64.length : fileData.file?.size || 0;
        if (dataSize < 1000 && fileData.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
          console.error(`[UploadQueue] Suspiciously small image in queue: ${fileData.filename} (${dataSize} bytes/chars), removing`);
          await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
          await uploadQueueDB.uploads.delete(upload.id);
          return;
        }
      }

      // Build FormData
      const formData = new FormData();

      // Add data fields
      Object.keys(upload.data).forEach(key => {
        const value = upload.data[key];
        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Add files - convert from base64 back to Blob for upload
      for (const fileData of files) {
        let blob: Blob;

        // Use base64 if available (new format), otherwise fall back to stored blob
        if (fileData.fileBase64) {
          blob = this.base64ToBlob(fileData.fileBase64, 'image/jpeg');
          console.log(`[UploadQueue] Converted base64 to blob: ${fileData.filename}, ${blob.size} bytes`);
        } else {
          blob = fileData.file;
          console.log(`[UploadQueue] Using stored blob: ${fileData.filename}, ${blob.size} bytes`);
        }

        if (blob.size === 0) {
          console.error(`[UploadQueue] File is 0 bytes, skipping: ${fileData.filename}`);
          continue;
        }

        formData.append(fileData.fieldName, blob, fileData.filename);
      }

      // Upload based on type
      let response;
      switch (upload.type) {
        case 'photo':
          response = await api.post(API_CONFIG.ENDPOINTS.DAILY_REPORTS + '/photos', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'asphalt_photo':
          response = await api.post('/api/asphalt-reports/photos', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'truck_ticket':
          response = await api.post(API_CONFIG.ENDPOINTS.TRUCKS, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'daily_report':
          response = await api.post(API_CONFIG.ENDPOINTS.DAILY_REPORTS, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'tm_ticket':
          response = await api.post(API_CONFIG.ENDPOINTS.TM_TICKETS, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'equipment_issue':
          response = await api.post(API_CONFIG.ENDPOINTS.EQUIPMENT_ISSUE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'asphalt_report':
          response = await api.post('/api/asphalt-reports', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;

        case 'receipt':
          response = await api.post('/api/receipts', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          break;
      }

      if (response?.data.success) {
        // Delete the upload and associated files immediately after success
        await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
        await uploadQueueDB.uploads.delete(upload.id);
        console.log(`Upload ${upload.id} completed and removed from queue`);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error(`Upload ${upload.id} failed:`, error);

      // Check if it's a network error (no internet) vs a real server error
      const isNetworkError =
        !navigator.onLine ||
        error.message?.includes('Network') ||
        error.message?.includes('Failed to fetch') ||
        error.code === 'ERR_NETWORK';

      if (isNetworkError) {
        // Network errors: keep retrying, don't increment counter
        await uploadQueueDB.uploads.update(upload.id, {
          status: 'pending',
          error: 'Waiting for internet connection',
          retryCount: upload.retryCount // Don't increment for network errors
        });
      } else {
        // Real server errors: increment retry count
        const newRetryCount = upload.retryCount + 1;

        if (newRetryCount >= 3) {
          // After 3 failed attempts with real errors, delete the upload
          console.log(`Upload ${upload.id} failed 3 times with server errors, removing from queue`);
          await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
          await uploadQueueDB.uploads.delete(upload.id);
        } else {
          await uploadQueueDB.uploads.update(upload.id, {
            status: 'pending',
            error: error.message || 'Upload failed',
            retryCount: newRetryCount
          });
        }
      }
    }
  }

  /**
   * Start auto-sync interval (every 30 seconds)
   * Also sets up app lifecycle listeners for background/foreground transitions
   */
  async startAutoSync() {
    if (this.syncInterval) return;

    // Fix any uploads stuck in "uploading" status (from previous session crashes)
    await this.fixStuckUploads();

    this.syncInterval = setInterval(() => {
      this.processQueue();
    }, 30000); // 30 seconds

    // Set up app lifecycle listeners (native app only)
    this.initAppListeners();

    // Initial sync
    this.processQueue();
  }

  /**
   * Initialize app lifecycle listeners for background/foreground transitions
   * Triggers upload queue when app resumes or becomes visible
   */
  private initAppListeners() {
    if (this.appListenersInitialized) return;
    this.appListenersInitialized = true;

    // Native app: Listen for app state changes
    if (isNativeApp()) {
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('[UploadQueue] App became active, processing pending uploads...');
          // Retry any native uploads that failed due to network (file path queue)
          await backgroundUploadService.retryPendingNativeUploads();
          // Finalize any uploads that completed while app was backgrounded
          await backgroundUploadService.finalizePendingUploads();
          this.processQueue();
        }
      });

      App.addListener('resume', async () => {
        console.log('[UploadQueue] App resumed from background, processing pending uploads...');
        // Retry any native uploads that failed due to network (file path queue)
        await backgroundUploadService.retryPendingNativeUploads();
        // Finalize any uploads that completed while app was backgrounded
        await backgroundUploadService.finalizePendingUploads();
        this.processQueue();
      });

      // Initialize background upload service
      backgroundUploadService.initialize();

      console.log('[UploadQueue] Native app lifecycle listeners initialized');
    }

    // PWA/Web: Listen for visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('[UploadQueue] Page became visible, processing queue...');
          this.processQueue();
        }
      });

      // Also trigger on online event
      window.addEventListener('online', () => {
        console.log('[UploadQueue] Network came online, processing queue...');
        this.processQueue();
      });

      console.log('[UploadQueue] Web visibility listeners initialized');
    }
  }

  /**
   * Fix uploads stuck in "uploading" status
   */
  private async fixStuckUploads() {
    try {
      const stuckUploads = await uploadQueueDB.uploads
        .where('status')
        .equals('uploading')
        .toArray();

      for (const upload of stuckUploads) {
        if (upload.id) {
          await uploadQueueDB.uploads.update(upload.id, {
            status: 'pending',
            retryCount: 0
          });
        }
      }

      if (stuckUploads.length > 0) {
        console.log(`Fixed ${stuckUploads.length} stuck uploads from previous session`);
      }
    } catch (error) {
      console.error('Failed to fix stuck uploads:', error);
    }
  }

  /**
   * Stop auto-sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Get pending upload for a specific type, job, and employee
   * Used to recover data when a report was queued but not yet uploaded
   */
  async getPendingUpload(
    type: 'daily_report' | 'asphalt_report' | 'equipment_issue',
    employeeId: number,
    jobId: number
  ): Promise<QueuedUpload | null> {
    try {
      // Find pending or uploading uploads for this type/job/employee
      const uploads = await uploadQueueDB.uploads
        .where('type')
        .equals(type)
        .and(upload =>
          upload.employeeId === employeeId &&
          upload.jobId === jobId &&
          (upload.status === 'pending' || upload.status === 'uploading' || upload.status === 'failed')
        )
        .toArray();

      if (uploads.length > 0) {
        // Return the most recent one
        uploads.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        console.log(`[UploadQueue] Found pending ${type} upload for job ${jobId}, employee ${employeeId}`);
        return uploads[0];
      }

      return null;
    } catch (error) {
      console.error('[UploadQueue] Error getting pending upload:', error);
      return null;
    }
  }

  /**
   * Get queue statistics
   * Combines IndexedDB queue (PWA) with native background upload queue (iOS/Android)
   */
  async getQueueStats() {
    // IndexedDB queue stats (primarily for PWA, fallback for native)
    const dbPending = await uploadQueueDB.uploads
      .where('status')
      .equals('pending')
      .count();

    const dbUploading = await uploadQueueDB.uploads
      .where('status')
      .equals('uploading')
      .count();

    const dbFailed = await uploadQueueDB.uploads
      .where('status')
      .equals('failed')
      .count();

    // Native background upload stats (iOS/Android only)
    const nativeStats = backgroundUploadService.getNativeQueueStats();

    // Combine stats:
    // - pending = IndexedDB pending + native pending retry (waiting for network)
    // - uploading = IndexedDB uploading + native in progress (uploaded, awaiting finalization)
    // - failed = IndexedDB failed only (native retries automatically)
    const pending = dbPending + nativeStats.pendingRetry;
    const uploading = dbUploading + nativeStats.inProgress;
    const failed = dbFailed;

    // Note: completed uploads are deleted immediately, so this will always be 0
    const completed = 0;

    return { pending, uploading, failed, completed, total: pending + uploading + failed };
  }

  /**
   * Clear failed uploads older than 7 days
   */
  async clearOldUploads() {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const oldUploads = await uploadQueueDB.uploads
      .where('status')
      .equals('failed')
      .and(upload => upload.createdAt && upload.createdAt < sevenDaysAgo)
      .toArray();

    for (const upload of oldUploads) {
      if (upload.id) {
        await uploadQueueDB.files.where('uploadId').equals(upload.id).delete();
        await uploadQueueDB.uploads.delete(upload.id);
      }
    }
  }

  /**
   * Retry a failed upload
   */
  async retryUpload(uploadId: number) {
    await uploadQueueDB.uploads.update(uploadId, {
      status: 'pending',
      error: undefined,
      retryCount: 0
    });

    this.processQueue();
  }

  /**
   * Delete an upload from queue
   */
  async deleteUpload(uploadId: number) {
    await uploadQueueDB.files.where('uploadId').equals(uploadId).delete();
    await uploadQueueDB.uploads.delete(uploadId);
  }

  /**
   * Get status of a specific upload by ID
   * Returns null if upload doesn't exist (means it completed successfully)
   */
  async getUploadStatus(uploadId: number): Promise<{
    status: 'pending' | 'uploading' | 'failed' | 'completed';
    error?: string;
    retryCount?: number;
  } | null> {
    const upload = await uploadQueueDB.uploads.get(uploadId);
    if (!upload) {
      // Upload was deleted, which means it completed successfully
      return { status: 'completed' };
    }
    return {
      status: upload.status,
      error: upload.error,
      retryCount: upload.retryCount
    };
  }

  /**
   * Wait for a specific upload to complete (with timeout)
   * Resolves when upload completes or fails, or after timeout
   */
  async waitForUpload(uploadId: number, timeoutMs: number = 60000): Promise<{
    success: boolean;
    status: 'completed' | 'failed' | 'timeout' | 'pending';
    error?: string;
  }> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      const status = await this.getUploadStatus(uploadId);

      if (!status || status.status === 'completed') {
        return { success: true, status: 'completed' };
      }

      if (status.status === 'failed') {
        return { success: false, status: 'failed', error: status.error };
      }

      // Wait 500ms before checking again
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Timeout reached
    const finalStatus = await this.getUploadStatus(uploadId);
    return {
      success: false,
      status: finalStatus?.status === 'completed' ? 'completed' : 'timeout'
    };
  }

  /**
   * Convert Blob to base64 string for storage
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Return just the base64 data without the data URL prefix
        const base64Data = base64.split(',')[1] || base64;
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Convert base64 string back to Blob for upload
   */
  private base64ToBlob(base64: string, mimeType: string = 'image/jpeg'): Blob {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mimeType });
    } catch (error) {
      console.error('[UploadQueue] base64ToBlob error:', error);
      return new Blob();
    }
  }
}

export const uploadQueueService = new UploadQueueService();
